import { useEffect, useState } from "react"
import {
    getCartTotal,
    clearCart
  } from "../../redux/CartSlice";
  import { useDispatch, useSelector } from "react-redux";
import ScrollToTop from "../../components/ScrollToTop";
import Heading from "../../common/Heading";
import { Link, useLocation } from "react-router-dom";
import "./confirmation.css";
import Seo from "../../components/Seo";


import { routeMeta } from "../../seo/staticRoutes";
import { CUSTOMER_RECEIPT_ENABLED } from "../../util/OrderNotification";
export default function Confirmation(){
    const dispatch = useDispatch();
    // The real Firestore document id, handed over by Cart.js. This used to be
    // Math.random(), so every customer quoted a reference number that existed
    // nowhere in the order book.
    const { state } = useLocation();
    const orderId = state?.orderId;
    const orderTotal = state?.total;
    const paymentMethod = state?.paymentMethod;
    const customerEmail = state?.email;
    const receiptSent = CUSTOMER_RECEIPT_ENABLED && Boolean(customerEmail);
    const [showAnimation, setShowAnimation] = useState(false);

    useEffect(()=>{
        dispatch(clearCart());
        dispatch(getCartTotal());
        // Trigger animation after component mounts
        setTimeout(() => setShowAnimation(true), 300);
    },[])
    
    return(
        <>
            <Seo {...routeMeta("/confirmation")} />
            <Heading title="Home" subtitle="Order Confirmation" />
            
            <div className="professional-confirmation-container">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-lg-8 col-md-10">
                            <div className={`confirmation-card ${showAnimation ? 'animate-in' : ''}`}>
                                {/* Success Icon */}
                                <div className="success-icon-container">
                                    <div className="success-icon">
                                        <i className="fas fa-check"></i>
                                    </div>
                                    <div className="success-ripple"></div>
                                    <div className="success-ripple delay-1"></div>
                                    <div className="success-ripple delay-2"></div>
                                </div>
                                
                                {/* Main Content */}
                                <div className="confirmation-content">
                                    <h1 className="confirmation-title">
                                        Order Placed Successfully!
                                    </h1>
                                    <p className="confirmation-subtitle">
                                        Thank you for choosing NT Sticks. Your order has been received and is being processed.
                                    </p>

                                    {/* Damage and missing-item claims are assessed from an unboxing
                                        video, and a video cannot be recorded after the parcel is open.
                                        Stating this only in the returns policy would mean most customers
                                        first read it once it is already too late to comply — so it is
                                        surfaced here, the one customer-facing screen between ordering
                                        and delivery. The order email goes to NT Sticks, not the buyer. */}
                                    <div className="unboxing-notice">
                                        <i className="fas fa-video" aria-hidden="true"></i>
                                        <div>
                                            <strong>Please film your unboxing</strong>
                                            <span>
                                                Start recording before you open the parcel and keep the clip
                                                until you have checked everything. Claims for damage, missing
                                                items or a wrong product are assessed from that video and must
                                                reach us within 48&nbsp;hours of delivery.
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* The customer chose to pay by UPI, so this is the
                                        only screen that can tell them what to send and
                                        what to quote. Without the order number on the
                                        payment, an incoming transfer cannot be matched
                                        to an order. */}
                                    {paymentMethod === "Prepayment" && (
                                        <div className="payment-due-notice">
                                            <i className="fas fa-qrcode" aria-hidden="true"></i>
                                            <div>
                                                <strong>Your order is reserved — please send ₹{orderTotal} by UPI</strong>
                                                <span>
                                                    Scan the QR on the cart page or pay to our UPI number,
                                                    and put <strong>{orderId}</strong> in the payment note.
                                                    We'll confirm and dispatch as soon as it arrives. If
                                                    you'd rather pay on delivery, WhatsApp us on 6264208349
                                                    and we'll switch it over.
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Order Details */}
                                    <div className="order-details-card">
                                        <div className="order-header">
                                            <h3 className="order-title">
                                                <i className="fas fa-receipt mr-2"></i>
                                                Order Details
                                            </h3>
                                        </div>
                                        <div className="order-info">
                                            <div className="info-item">
                                                <span className="info-label">Order Number:</span>
                                                <span className="info-value order-id">{orderId || "—"}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Order Date:</span>
                                                <span className="info-value">{new Date().toLocaleDateString('en-IN', { 
                                                    year: 'numeric', 
                                                    month: 'long', 
                                                    day: 'numeric' 
                                                })}</span>
                                            </div>
                                            <div className="info-item">
                                                <span className="info-label">Estimated Delivery:</span>
                                                <span className="info-value">3-5 Business Days</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* What's Next */}
                                    <div className="next-steps">
                                        <h4 className="steps-title">
                                            <i className="fas fa-route mr-2"></i>
                                            What's Next?
                                        </h4>
                                        <div className="steps-list">
                                            {/* Only promised when a receipt is genuinely
                                                going out. The buyer previously received
                                                nothing at all while this told them to
                                                expect an email — so they waited, then
                                                called. */}
                                            <div className="step-item">
                                                <div className="step-icon">
                                                    <i className={`fas ${receiptSent ? "fa-envelope" : "fa-bookmark"}`}></i>
                                                </div>
                                                <div className="step-content">
                                                    {receiptSent ? (
                                                        <>
                                                            <h6>Order Confirmation Email</h6>
                                                            <p>We've sent your order details to {customerEmail}.</p>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h6>Save your order number</h6>
                                                            <p>
                                                                Note down {orderId ? <strong>{orderId}</strong> : "your order number"} before
                                                                you leave this page — quote it on WhatsApp or a call and
                                                                we'll pull your order up straight away.
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="step-item">
                                                <div className="step-icon">
                                                    <i className="fas fa-box"></i>
                                                </div>
                                                <div className="step-content">
                                                    <h6>Order Processing</h6>
                                                    <p>We'll carefully prepare your premium incense products for shipment.</p>
                                                </div>
                                            </div>
                                            <div className="step-item">
                                                <div className="step-icon">
                                                    <i className="fas fa-truck"></i>
                                                </div>
                                                <div className="step-content">
                                                    <h6>Shipping & Delivery</h6>
                                                    <p>Your order will be shipped and delivered within 3-5 business days.</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons */}
                                    <div className="confirmation-actions">
                                        <Link to="/" className="btn btn-primary-custom">
                                            <i className="fas fa-home mr-2"></i>
                                            Continue Shopping
                                        </Link>
                                        <Link to="/shop" className="btn btn-secondary-custom">
                                            <i className="fas fa-store mr-2"></i>
                                            Explore Products
                                        </Link>
                                    </div>
                                    
                                    {/* Contact Support */}
                                    <div className="support-section">
                                        <p className="support-text">
                                            <i className="fas fa-headset mr-2"></i>
                                            Need help? Contact us at{" "}
                                            <a href="mailto:ntincensesticks@gmail.com" className="support-link">
                                                ntincensesticks@gmail.com
                                            </a>
                                            {" "}or call{" "}
                                            <a href="tel:+916264208349" className="support-link">
                                                +91 6264208349
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ScrollToTop />
        </>
    )
}